import { StateSetter } from 'react';
import { useLang } from 'hooks/lang';

import { IconButton, Card, Table, Tag, Row, Tooltip } from 'components/Blocks';

import { PencilIcon } from 'components/Icons';

import { booleanRenders, FormTeamService } from './Form';

import { ServiceStatus } from './Status';
import { createExpression } from './calculations';

import { scrollToWrapperTop } from 'utils/html';
import { bestEffortSortCompare, sortByName } from 'utils/array';

import { useTeamProfile } from '../context';

export function ServicesTable({
  setFocusedService,
}: {
  setFocusedService: StateSetter<FormTeamService | null>;
}) {
  const { team, canMutateEntities } = useTeamProfile();

  const { t } = useLang();

  return (
    <Card>
      <Table
        items={team.services}
        uniqueKey="id"
        itemsPerPage={20}
        columns={[
          {
            className: 'w-12',
            render: (service) => <ServiceStatus service={service} />,
            sort: true,
            sorter: (a, b) => bestEffortSortCompare(b.disabled, a.disabled),
          },
          {
            label: t('name'),
            property: 'name',
            sort: true,
            search: true,
          },
          {
            label: t('tags'),
            render: ({ tags }) => (
              <Row gap="1" flexWrap="wrap" className="max-w-80">
                {tags.map((tag) => (
                  <Tag {...tag} key={tag.id} size="tiny" />
                ))}
              </Row>
            ),
          },
          {
            label: t('variables'),
            search: true,
            render: (service) =>
              sortByName(service.costVariables)
                .map((c) => c.name)
                .join(', '),
          },
          {
            label: t('cost'),
            render: (service) => createExpression(service),
          },
          {
            label: t('extensions'),
            render: (service) => (
              <Row gap="1">
                {booleanRenders
                  .filter((p) => service[p.property])
                  .map(({ Icon, getLabel, property }) => (
                    <Tooltip key={property} ensurePresence content={getLabel(t)}>
                      <Icon color="primary" size={14} />
                    </Tooltip>
                  ))}
              </Row>
            ),
          },
          {
            className: 'w-12',
            hide: !canMutateEntities,
            render: (service) => (
              <IconButton
                tooltipContent={t('update')}
                onClick={() => {
                  setFocusedService(service);
                  scrollToWrapperTop();
                }}
              >
                <PencilIcon size={14} />
              </IconButton>
            ),
          },
        ]}
      />
    </Card>
  );
}
